import React, { useEffect, useState } from 'react';
import { Box, Typography, Divider, CircularProgress, IconButton, Chip, Button, Alert } from '@mui/material';
import { Album, Artist, Track } from '@/types';
import TrackList from './TrackList';
import { fetchAlbum } from '@/services/albumService';
import { PLACEHOLDER_ALBUM } from '@/services/mediaService';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useAudio } from '@/context/AudioProvider';
import { useAuth } from '@/context/AuthProvider';
import { canPlayAlbum } from '@/services/accessService';
import { useNavigate } from 'react-router-dom';

interface Props {
  albums: Album[];
  artist: Artist | null;
  onAlbumsLoaded?: (firstPlayableAlbum: Album | null) => void;
  registerAlbumRef?: (id: string, element: HTMLElement | null) => void;
  registerTrackRef?: (id: string, element: HTMLElement | null) => void;
}

const ArtistAlbums: React.FC<Props> = ({ 
  albums, 
  artist, 
  onAlbumsLoaded,
  registerAlbumRef,
  registerTrackRef
}) => {
  const [loadedAlbums, setLoadedAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [albumAccess, setAlbumAccess] = useState<Record<string, { canPlay: boolean; reason?: string }>>({});
  const { playTrack, togglePlay, state } = useAudio();
  const { isPlaying, currentTrack } = state;
  const { token, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!Array.isArray(albums) || albums.length === 0) {
      setLoading(false);
      return;
    }

    const loadAlbumDetails = async () => {
      try {
        // FIX: If albums already have tracks (from parent), use them directly
        // Only fetch if albums are missing track data
        const detailedAlbums = await Promise.all(
          albums.map(async (album) => {
            // If album already has tracks, use it as-is
            if (album.tracks && album.tracks.length > 0) {
              console.log(`✅ Album "${album.title}" already has ${album.tracks.length} tracks, skipping fetch`);
              return album;
            }
            // Otherwise fetch the full album details
            console.log(`🔍 Fetching details for album "${album.title}"`);
            return fetchAlbum(album.id);
          })
        );
        setLoadedAlbums(detailedAlbums);

        // Check access for each album
        const accessChecks = await Promise.all(
          detailedAlbums.map(async (album) => {
            const access = await canPlayAlbum(album.id, album.access_type || 'standard', token || undefined);
            return { albumId: album.id, access };
          })
        );

        const accessMap: Record<string, { canPlay: boolean; reason?: string }> = {};
        accessChecks.forEach(({ albumId, access }) => {
          accessMap[albumId] = access;
        });
        setAlbumAccess(accessMap);

        setLoading(false);

        const firstPlayableAlbum = detailedAlbums.find(
          album => album?.tracks && album.tracks.length > 0 && accessMap[album.id]?.canPlay
        ) || null;

        onAlbumsLoaded?.(firstPlayableAlbum);
      } catch (error) {
        console.error('Error loading album details:', error);
        setLoading(false);
      }
    };

    loadAlbumDetails();
  }, [albums, onAlbumsLoaded, token]);

  useEffect(() => {
    if ((!albums || albums.length === 0) && artist) {
      console.warn('[ArtistAlbums] No albums available for artist:', artist?.name || 'Unknown');
    }
  }, [albums, artist?.id]);

  const handlePlayAlbum = async (album: Album) => {
    if (!album?.tracks || album.tracks.length === 0) {
      alert('No tracks available for this album');
      return;
    }

    const firstTrack = album.tracks[0];
    if (!firstTrack) return;

    // Check track-level access control (from bundle endpoint)
    const trackAccessType = (firstTrack as any).access_type || album.access_type || 'standard';
    const trackAccessGranted = (firstTrack as any).access_granted === true;
    
    // For standard tracks, require active subscription
    if (trackAccessType === 'standard') {
      if (!hasActiveSubscription) {
        alert('Active subscription required to play this track.');
        navigate('/subscribe');
        return;
      }
      // Even with subscription, if API explicitly denied access, block playback
      if ((firstTrack as any).access_granted === false) {
        alert((firstTrack as any).access_reason || 'Access denied');
        return;
      }
    }
    
    // For premium tracks (soundcheck/vip/limited), require explicit access_granted === true
    if (trackAccessType === 'soundcheck' || trackAccessType === 'vip' || trackAccessType === 'limited') {
      if (!trackAccessGranted) {
        alert('This track requires SoundCheck access. Please visit the album page to get access.');
        navigate(`/album/${album.id}`);
        return;
      }
    }
    
    // For free tracks, allow playback (access_granted should be true)
    if (trackAccessType === 'free' && !trackAccessGranted) {
      alert((firstTrack as any).access_reason || 'Access denied');
      return;
    }

    const access = albumAccess[album.id];
    if (!access?.canPlay) {
      if (album.access_type === 'free' || album.access_type === 'standard') {
        alert(access?.reason || 'Access denied');
      } else {
        // Navigate to purchase page for premium content
        navigate(`/checkout/album/${album.id}`);
      }
      return;
    }

    if (firstTrack) {
      const tracksWithMeta = album.tracks.map(track => ({
        ...track,
        albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
        albumName: album.title,
        artistName: artist?.name,
        artistProfileImage: artist?.image_url,
      }));
      
      const firstTrackWithMeta = {
        ...firstTrack,
        albumArtwork: album.artwork_url || PLACEHOLDER_ALBUM,
        albumName: album.title,
        artistName: artist?.name,
        artistProfileImage: artist?.image_url,
      };
      
      playTrack(firstTrackWithMeta, tracksWithMeta);
    }
  };

  const getAccessTypeColor = (accessType: string) => {
    switch (accessType) {
      case 'free': return 'success';
      case 'standard': return 'primary';
      case 'soundcheck': return 'warning';
      case 'vip': return 'error';
      case 'limited': return 'secondary';
      default: return 'default';
    }
  };

  const getAccessTypeLabel = (accessType: string) => {
    switch (accessType) {
      case 'free': return 'Free';
      case 'standard': return 'Standard';
      case 'soundcheck': return 'Soundcheck';
      case 'vip': return 'VIP';
      case 'limited': return 'Limited';
      default: return 'Unknown';
    }
  };

  if (!artist) return null;

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (!Array.isArray(loadedAlbums) || loadedAlbums.length === 0) {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          Releases
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No releases found for this artist.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="h5" gutterBottom>
        Releases
      </Typography>

      {loadedAlbums.map((album) => {
        if (!album || !album.id) return null;
        const hasTracks = album.tracks && album.tracks.length > 0;
        const isCurrentAlbumPlaying =
          isPlaying && currentTrack?.albumName === album.title;
        const access = albumAccess[album.id];
        const canPlay = access?.canPlay || false;
        const accessType = album.access_type || 'standard';

        return (
          <Box 
            key={album.id} 
            sx={{ mt: 3, mb: 3 }}
            id={`album-${album.id}`}
            ref={el => registerAlbumRef?.(album.id, el as HTMLElement | null)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                position: "relative",
                mb: 2,
              }}
            >
              <Box sx={{ position: 'relative', width: 60, height: 60, mr: 2 }}>
                <Box
                  component="img"
                  src={album.artwork_url || PLACEHOLDER_ALBUM}
                  alt={album.title}
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1,
                    objectFit: 'cover'
                  }}
                />
                {hasTracks && (
                  <IconButton 
                    onClick={() => handlePlayAlbum(album)}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      },
                      padding: '4px',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    {canPlay ? (
                      isCurrentAlbumPlaying ? (
                        <PauseIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )
                    ) : (
                      <ShoppingCartIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography 
                    variant="h6"
                    onClick={() => navigate(`/artist/${artist.slug || artist.id}/album/${album.slug || album.id}`)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.main'
                      }
                    }}
                  >
                    {album.title || 'Untitled Album'}
                  </Typography>
                  <Chip 
                    label={getAccessTypeLabel(accessType)}
                    color={getAccessTypeColor(accessType)}
                    size="small"
                    variant={canPlay ? "filled" : "outlined"}
                  />
                  {canPlay && accessType !== 'free' && accessType !== 'standard' && (
                    <Chip 
                      label="✓ Owned"
                      color="success"
                      size="small"
                      variant="filled"
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Released: {album.release_date || 'Unknown date'}
                </Typography>
                
                {!canPlay && accessType === 'soundcheck' && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => navigate(`/checkout/album/${album.id}`)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Purchase Soundcheck Access
                    </Button>
                  </Box>
                )}
                
                {!canPlay && accessType !== 'soundcheck' && accessType !== 'free' && accessType !== 'standard' && (
                  <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {access?.reason || 'Premium access required'}
                  </Alert>
                )}
              </Box>
            </Box>

            {hasTracks && (
              <>
                <Divider sx={{ my: 2 }} />
                <TrackList
                  tracks={album.tracks || []}
                  album={album}
                  artist={artist}
                  canPlay={canPlay}
                  accessReason={access?.reason}
                  registerTrackRef={registerTrackRef}
                />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ArtistAlbums;



